import React from 'react';
import Modal from '@mui/material/Modal';
import { tokens } from "../../theme";
import { Box, useTheme, Typography, Divider, CircularProgress, Chip } from "@mui/material";
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';

const StudentHomeworkHistoryModal = ({ open, handleClose, student, courseId, courseName }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [loading, setLoading] = React.useState(false);
    const [homeworkHistory, setHomeworkHistory] = React.useState([]);
    const [error, setError] = React.useState(null);

    console.log("StudentHomeworkHistoryModal props:", { open, student, courseId, courseName });

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: 1200,
        maxHeight: '80vh',
        bgcolor: colors.primary[750],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        overflow: 'auto'
    };

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        backgroundColor: colors.primary[300],
        color: theme.palette.common.white,
        fontSize: 14,
        fontWeight: "bold",
        border: '1px solid rgba(224, 224, 224, 1)',
    }));

    const StyledBodyCell = styled(TableCell)(({ theme }) => ({
        fontSize: 14,
        border: '1px solid rgba(224, 224, 224, 1)',
    }));

    const StyledTableRow = styled(TableRow)(({ theme }) => ({
        '&:nth-of-type(odd)': {
            backgroundColor: colors.primary[800],
        },
        '&:nth-of-type(even)': {
            backgroundColor: colors.primary[750],
        },
    }));

    React.useEffect(() => {
        if (open && student && courseId) {
            fetchHomeworkHistory();
        }
    }, [open, student, courseId]);

    const fetchHomeworkHistory = async () => {
        if (!student || !courseId || !student.uuid) {
            setError("Invalid student or course information.");
            return;
        };

        setLoading(true);
        setError(null);
        try {
            // Fetch homework history using the dedicated endpoint
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/students/${student.uuid}/homework-history`,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );

            if (response.data && response.data.homeworkHistory) {
                setHomeworkHistory(response.data.homeworkHistory);
            }
        } catch (err) {
            console.error("Error fetching homework history:", err);
            setError("Failed to load homework history. Please try again.");
        } finally {
            setLoading(false);
        }
    };


    const getStatusChip = (status, statusColor) => {
        return (
            <Chip 
                label={status} 
                color={statusColor}
                size="small"
                sx={{ fontWeight: 'bold' }}
            />
        );
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="homework-history-modal"
            aria-describedby="student-homework-history"
        >
            <Box sx={style}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AssignmentIcon fontSize='large' />
                        <Typography id="homework-history-modal" variant="h3" fontWeight="bold">
                            Homework History
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: colors.grey[600] }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" color={colors.greenAccent[400]}>
                        Student: {student?.name}
                    </Typography>
                    <Typography variant="h6" color={colors.grey[700]}>
                        Course: {courseName}
                    </Typography>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>
                        {error}
                    </Typography>
                ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: '50vh' }}>
                        <Table stickyHeader aria-label="homework history table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell style={{ width: "5%" }}>#</StyledTableCell>
                                    <StyledTableCell style={{ width: "30%" }}>Homework Title</StyledTableCell>
                                    <StyledTableCell style={{ width: "15%" }}>Created Date</StyledTableCell>
                                    <StyledTableCell style={{ width: "15%" }}>Due Date</StyledTableCell>
                                    <StyledTableCell style={{ width: "15%" }}>Submission Date</StyledTableCell>
                                    <StyledTableCell style={{ width: "15%" }}>Status</StyledTableCell>
                                    {/* <StyledTableCell style={{ width: "10%" }}>Score</StyledTableCell> */}
                                    <StyledTableCell style={{ width: "15%" }}>Review Feedback</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {homeworkHistory.length === 0 ? (
                                    <StyledTableRow>
                                        <StyledBodyCell colSpan={8} align="center">
                                            No homework history found for this student
                                        </StyledBodyCell>
                                    </StyledTableRow>
                                ) : (
                                    homeworkHistory.map((homework, index) => (
                                        <StyledTableRow key={homework.id}>
                                            <StyledBodyCell>{index + 1}</StyledBodyCell>
                                            <StyledBodyCell>{homework.title}</StyledBodyCell>
                                            <StyledBodyCell>
                                                {dayjs(homework.createdAt).format('MMM DD, YYYY')}
                                            </StyledBodyCell>
                                            <StyledBodyCell>
                                                {dayjs(homework.dueDate).format('MMM DD, YYYY')}
                                            </StyledBodyCell>
                                            <StyledBodyCell>
                                                {homework.submissionDate ? 
                                                    dayjs(homework.submissionDate).format('MMM DD, YYYY') : 
                                                    '-'
                                                }
                                            </StyledBodyCell>
                                            <StyledBodyCell>
                                                {getStatusChip(homework.status, homework.statusColor)}
                                            </StyledBodyCell>
                                            {/* <StyledBodyCell align="center">
                                                {homework.score || '-'}
                                            </StyledBodyCell> */}
                                            <StyledBodyCell align="center">
                                                {homework.feedback?.is_opened ? 
                                                    <Chip label="Reviewed" color="info" size="small" /> : 
                                                    '-'
                                                }
                                            </StyledBodyCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Modal>
    );
};

export default StudentHomeworkHistoryModal;